import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Settings, Building2, Image as ImageIcon, Plus, Edit, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ConfirmModal from '@/components/ConfirmModal';

const Configuracoes = () => {
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogBanco, setDialogBanco] = useState(false);
  const [editingBanco, setEditingBanco] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, id: null });
  const [formBanco, setFormBanco] = useState({
    nome: '',
    saldo_inicial: 0
  });

  useEffect(() => {
    loadBancos();
  }, []);

  const loadBancos = async () => {
    try {
      const response = await api.get('/bancos');
      setBancos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar bancos');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBanco = async (e) => {
    e.preventDefault();
    try {
      if (editingBanco) {
        // Atualizar nome do banco
        await api.put(`/bancos/${editingBanco.id}/nome?nome=${encodeURIComponent(formBanco.nome)}`);
        toast.success('Banco atualizado! Todas movimentações foram atualizadas.');
      } else {
        await api.post('/bancos', formBanco);
        toast.success('Banco adicionado! Já disponível em Entradas/Saídas e Contas.');
      }
      setDialogBanco(false);
      setEditingBanco(null);
      setFormBanco({ nome: '', saldo_inicial: 0 });
      loadBancos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar banco');
    }
  };

  const handleEdit = (banco) => {
    setEditingBanco(banco);
    setFormBanco({ nome: banco.nome, saldo_inicial: banco.saldo_inicial });
    setDialogBanco(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/bancos/${id}`);
      toast.success('Banco removido!');
      loadBancos();
    } catch (error) {
      toast.error('Erro ao remover banco');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="space-y-6 animate-slide-up">
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, id: null })}
        onConfirm={() => handleDelete(confirmDelete.id)}
        title="Remover Banco"
        message="Deseja realmente remover este banco? Todas as movimentações vinculadas ficarão sem banco."
        confirmText="Sim, Remover"
        cancelText="Cancelar"
      />

      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center">
          <Settings className="w-8 h-8 mr-3 text-slate-600" />
          Configurações
        </h1>
        <p className="text-slate-600">Gerencie bancos, logomarcas e outras configurações do sistema</p>
      </div>

      <Tabs defaultValue="bancos" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="bancos">Bancos</TabsTrigger>
          <TabsTrigger value="logos">Logomarcas</TabsTrigger>
        </TabsList>

        {/* Tab Bancos */}
        <TabsContent value="bancos" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">{bancos.length} bancos cadastrados</p>
            <Button 
              onClick={() => { setEditingBanco(null); setFormBanco({ nome: '', saldo_inicial: 0 }); setDialogBanco(true); }}
              className="bg-gradient-to-r from-green-600 to-emerald-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Banco
            </Button>
          </div>

          {/* Modal Adicionar/Editar Banco */}
          {dialogBanco && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogBanco(false)}>
              <div className="bg-white rounded-xl shadow-2xl max-w-md w-full m-4" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
                  <h2 className="text-xl font-bold">{editingBanco ? 'Editar Banco' : 'Adicionar Banco'}</h2>
                  <button onClick={() => setDialogBanco(false)} className="p-2 hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleSaveBanco} className="p-6 space-y-4">
                  <div>
                    <Label>Nome do Banco</Label>
                    <Input
                      value={formBanco.nome}
                      onChange={(e) => setFormBanco({ ...formBanco, nome: e.target.value })}
                      placeholder="Ex: Banco do Brasil, Nubank, etc"
                      required
                    />
                  </div>

                  {!editingBanco && (
                    <div>
                      <Label>Saldo Inicial (opcional)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formBanco.saldo_inicial}
                        onChange={(e) => setFormBanco({ ...formBanco, saldo_inicial: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Deixe 0 se não souber o saldo inicial
                      </p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setDialogBanco(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-green-600 to-emerald-600">
                      Salvar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Lista de Bancos */}
          <div className="space-y-3">
            {bancos.map((banco) => (
              <Card key={banco.id} className="card-hover">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{banco.nome}</h3>
                        <p className="text-sm text-slate-600">Saldo: {formatCurrency(banco.saldo_atual)}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(banco)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => setConfirmDelete({ isOpen: true, id: banco.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tab Logomarcas */}
        <TabsContent value="logos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <ImageIcon className="w-5 h-5 mr-2 text-slate-600" />
                Logomarcas do Sistema
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base font-semibold mb-3 block">Logo Principal (Colorida)</Label>
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/yby7ux02_logoLogin1.png"
                    alt="Logo Principal"
                    className="w-24 h-24 object-contain border border-slate-200 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 mb-2">
                      <strong>Usado em:</strong> Tela de login, recibos PDF
                    </p>
                    <Input
                      placeholder="Cole a URL da nova logo..."
                      defaultValue="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/yby7ux02_logoLogin1.png"
                      className="text-xs"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Logo Branca</Label>
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/n64g8vmv_LOGO%20BRANCA.png"
                    alt="Logo Branca"
                    className="w-24 h-24 object-contain border border-slate-200 rounded bg-slate-800"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 mb-2">
                      <strong>Usado em:</strong> Tela de login (fundo vermelho)
                    </p>
                    <Input
                      placeholder="Cole a URL da nova logo branca..."
                      defaultValue="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/n64g8vmv_LOGO%20BRANCA.png"
                      className="text-xs"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Favicon / PWA Icon</Label>
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/euo7581j_BoraCar%20-%20Logo%2014%20-%20Fundo%20verde.png"
                    alt="Favicon"
                    className="w-24 h-24 object-contain border border-slate-200 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 mb-2">
                      <strong>Usado em:</strong> Aba do navegador, ícone do app instalado
                    </p>
                    <Input
                      placeholder="Cole a URL do novo favicon..."
                      defaultValue="https://customer-assets.emergentagent.com/job_financeiro-boracar/artifacts/euo7581j_BoraCar%20-%20Logo%2014%20-%20Fundo%20verde.png"
                      className="text-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>ℹ️ Como atualizar logos:</strong>
                </p>
                <ol className="text-xs text-blue-800 mt-2 space-y-1 ml-4 list-decimal">
                  <li>Faça upload da imagem para um serviço (ex: Imgur, GitHub)</li>
                  <li>Copie a URL pública da imagem</li>
                  <li>Cole no campo correspondente acima</li>
                  <li>Clique "Salvar Configurações" (em breve)</li>
                </ol>
              </div>

              <div className="flex justify-end">
                <Button className="bg-gradient-to-r from-blue-600 to-cyan-600" disabled>
                  Salvar Configurações
                  <span className="text-xs ml-2">(Em breve)</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Configuracoes;
